import {
  Alert,
  Button,
  Card,
  Heading,
  NumberField,
  Text,
  TextArea,
  TextField,
} from "@/design-system";
import { trackEvent } from "@/lib/dataLayer";
import { cnpjUtils } from "@/utils/cnpjUtils";
import { phoneUtils } from "@/utils/phone";
import { zodResolver } from "@hookform/resolvers/zod";
import { Eraser, Send } from "lucide-react";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { FORM_SECTION_ID } from "../constants";
import { useSellerDraft } from "../hooks/useSellerDraft";
import {
  partnerFormDefaults,
  partnerFormSchema,
  type PartnerFormValues,
} from "./partnerFormSchema";

type SubmitState = "idle" | "sent" | "failed";

/**
 * Formulário de indicação: o vendedor da Alpha7 cadastra a farmácia e os seus
 * próprios dados. Vai por e-mail para a Fidexa (api/alpha7-lead.ts), igual ao
 * formulário de demonstração da home.
 */
export const PartnerForm = () => {
  const [submitState, setSubmitState] = useState<SubmitState>("idle");
  const sellerDraft = useSellerDraft();
  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<PartnerFormValues>({
    resolver: zodResolver(partnerFormSchema),
    mode: "onTouched",
    defaultValues: { ...partnerFormDefaults, ...sellerDraft.initial },
  });

  const clearForm = () => {
    sellerDraft.clear();
    reset(partnerFormDefaults);
    setSubmitState("idle");
  };

  const onSubmit = async (data: PartnerFormValues) => {
    setSubmitState("idle");
    try {
      const response = await fetch("/api/alpha7-lead", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          clientTradeName: data.clientTradeName,
          clientContactName: data.clientContactName,
          clientWhatsapp: phoneUtils.cleanPhoneNumber(data.clientWhatsapp),
          clientCnpj: cnpjUtils.normalizeCNPJ(data.clientCnpj),
          storesCount: data.storesCount,
          sellerName: data.sellerName,
          sellerWhatsapp: phoneUtils.cleanPhoneNumber(data.sellerWhatsapp),
          sellerEmail: data.sellerEmail,
          notes: data.notes,
          source: "alpha7",
          honeypot: data.honeypot,
        }),
      });

      if (!response.ok) {
        throw new Error(`alpha7_lead_send_failed_${response.status}`);
      }

      trackEvent({
        event: "form_submit_partner",
        partner: "alpha7",
        stores_count: data.storesCount,
      });
      // Os dados do vendedor ficam para a próxima farmácia; só a farmácia e as
      // observações são limpas.
      const seller = {
        sellerName: data.sellerName,
        sellerWhatsapp: data.sellerWhatsapp,
        sellerEmail: data.sellerEmail,
      };
      sellerDraft.save(seller);
      reset({ ...partnerFormDefaults, ...seller });
      setSubmitState("sent");
    } catch (err) {
      console.error("[alpha7] submit failed", err);
      setSubmitState("failed");
    }
  };

  return (
    <section
      id={FORM_SECTION_ID}
      aria-labelledby="indicacao-title"
      className="mx-auto w-full max-w-6xl scroll-mt-24 px-4 sm:px-6 lg:px-8"
    >
      <div className="grid gap-8 lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)] lg:gap-12">
        <div className="flex flex-col gap-3 lg:sticky lg:top-24 lg:self-start">
          <Heading level={2} id="indicacao-title">
            Indique uma farmácia
          </Heading>
          <Text size="lead" tone="muted">
            Preencha os dados do cliente e os seus. A Fidexa entra em contato
            com o responsável pelo WhatsApp e avisa você do andamento.
          </Text>
          <Text size="small" tone="subtle">
            Campos marcados com * são obrigatórios.
          </Text>
        </div>

        <Card>
          <form
            onSubmit={handleSubmit(onSubmit)}
            noValidate
            className="flex flex-col gap-8"
          >
            <fieldset className="flex flex-col gap-4">
              <legend className="font-ds-sans text-ds-h4 text-ds-ink-900 mb-4 font-semibold">
                Dados da farmácia
              </legend>
              <TextField
                label="Cliente (nome fantasia)"
                required
                autoComplete="organization"
                placeholder="Ex.: Farmácia Central"
                error={errors.clientTradeName?.message}
                {...register("clientTradeName")}
              />
              <TextField
                label="Nome do responsável"
                required
                placeholder="Quem decide pela farmácia"
                error={errors.clientContactName?.message}
                {...register("clientContactName")}
              />
              <Controller
                control={control}
                name="clientWhatsapp"
                render={({ field }) => (
                  <TextField
                    label="WhatsApp do responsável"
                    required
                    type="tel"
                    inputMode="tel"
                    autoComplete="off"
                    placeholder="(11) 99999-9999"
                    error={errors.clientWhatsapp?.message}
                    name={field.name}
                    ref={field.ref}
                    value={field.value}
                    onBlur={field.onBlur}
                    onChange={e =>
                      field.onChange(phoneUtils.maskPhone(e.target.value))
                    }
                  />
                )}
              />
              <Controller
                control={control}
                name="clientCnpj"
                render={({ field }) => (
                  <TextField
                    label="CNPJ"
                    required
                    autoCapitalize="characters"
                    autoComplete="off"
                    placeholder="00.000.000/0000-00"
                    hint="Aceita o formato novo com letras (ex.: 01.CDW.WLA/0001-32). Validamos os dígitos verificadores ao sair do campo."
                    error={errors.clientCnpj?.message}
                    name={field.name}
                    ref={field.ref}
                    value={field.value}
                    onBlur={field.onBlur}
                    onChange={e =>
                      field.onChange(cnpjUtils.maskCNPJ(e.target.value))
                    }
                  />
                )}
              />
              <Controller
                control={control}
                name="storesCount"
                render={({ field }) => (
                  <NumberField
                    label="Quantidade de lojas"
                    decimals={0}
                    max={9999}
                    placeholder="Ex.: 3"
                    error={errors.storesCount?.message}
                    name={field.name}
                    ref={field.ref}
                    value={field.value}
                    onBlur={field.onBlur}
                    onValueChange={field.onChange}
                  />
                )}
              />
            </fieldset>

            <fieldset className="flex flex-col gap-4">
              <legend className="font-ds-sans text-ds-h4 text-ds-ink-900 mb-1 font-semibold">
                Seus dados (vendedor Alpha7)
              </legend>
              <Text size="small" tone="subtle" className="mb-3">
                Ficam salvos neste navegador para a próxima indicação.
              </Text>
              <TextField
                label="Nome do vendedor"
                required
                autoComplete="name"
                placeholder="Seu nome completo"
                error={errors.sellerName?.message}
                {...register("sellerName")}
              />
              <Controller
                control={control}
                name="sellerWhatsapp"
                render={({ field }) => (
                  <TextField
                    label="WhatsApp do vendedor"
                    required
                    type="tel"
                    inputMode="tel"
                    autoComplete="tel-national"
                    placeholder="(11) 99999-9999"
                    error={errors.sellerWhatsapp?.message}
                    name={field.name}
                    ref={field.ref}
                    value={field.value}
                    onBlur={field.onBlur}
                    onChange={e =>
                      field.onChange(phoneUtils.maskPhone(e.target.value))
                    }
                  />
                )}
              />
              <TextField
                label="E-mail do vendedor"
                required
                type="email"
                inputMode="email"
                autoComplete="email"
                placeholder="voce@a7.net.br"
                hint="É para este e-mail que a Fidexa responde."
                error={errors.sellerEmail?.message}
                {...register("sellerEmail")}
              />
            </fieldset>

            <TextArea
              label="Outra informação que julgue relevante"
              rows={4}
              maxLength={1000}
              placeholder="Ex.: a farmácia já usa crediário próprio há 5 anos e tem cerca de 400 clientes em atraso."
              error={errors.notes?.message}
              {...register("notes")}
            />

            <input
              type="text"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              className="hidden"
              {...register("honeypot")}
            />

            {submitState === "sent" ? (
              <Alert tone="success" title="Indicação enviada">
                Obrigado. A Fidexa entra em contato com o responsável e avisa
                você pelo e-mail informado.
              </Alert>
            ) : null}
            {submitState === "failed" ? (
              <Alert tone="danger" title="Não conseguimos enviar agora">
                Confira a conexão e tente de novo em instantes. Seus dados
                continuam preenchidos.
              </Alert>
            ) : null}

            <div className="flex flex-col gap-2 sm:flex-row-reverse sm:justify-start">
              <Button
                type="submit"
                variant="primary"
                size="lg"
                isLoading={isSubmitting}
                iconLeft={<Send />}
                className="w-full sm:w-auto"
              >
                Enviar indicação
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="lg"
                disabled={isSubmitting}
                onClick={clearForm}
                iconLeft={<Eraser />}
                className="w-full sm:w-auto"
              >
                Limpar formulário
              </Button>
            </div>
          </form>
        </Card>
      </div>
    </section>
  );
};
